/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useRef } from 'react'
import { useField, useForm } from 'react-final-form'
import { FieldValidator } from 'final-form'
import IconButton from '@mui/material/IconButton'
import PreviewIcon from '@mui/icons-material/Preview'
import { Modal, Popover } from '@mui/material'
import Button from '@mui/material/Button'
import { QuestionInvertTypeEnum } from '__generatedTypes__/globalTypes'
import Select from 'components/Select'
import { PageLoader } from 'components/PageLoader'
import * as classes from './styles'
import { lightModePreviewStyles, darkModePreviewStyles, DATA_ATTRIBUTE } from './styles'
import { prepareHtmlWithImages } from './invertImagesHelpers'

export const ITEMS = [
  {
    id: QuestionInvertTypeEnum.AUTO,
    title: 'Auto',
  },
  {
    id: QuestionInvertTypeEnum.YES,
    title: 'Yes',
  },
  {
    id: QuestionInvertTypeEnum.NO,
    title: 'No',
  },
]

export type Props = {
  name: string
  title: string
  validate?: FieldValidator<QuestionInvertTypeEnum>
  htmlWithImagesForPreview?: string
  className?: string
}

export const InvertImagesField = ({ name, title, validate, htmlWithImagesForPreview, className }: Props) => {
  const form = useForm()
  const { input, meta } = useField<QuestionInvertTypeEnum>(name, { validate })
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)
  const [processedHtml, setProcessedHtml] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [imagePopover, setImagePopover] = useState<{
    anchorEl: HTMLElement
    imageUrl: string
    invertMode: string
    isDarkMode: boolean
  } | null>(null)
  const lightModeRef = useRef<HTMLDivElement>(null)
  const darkModeRef = useRef<HTMLDivElement>(null)

  const errorMessage = meta.error || meta.submitError
  const error = meta.touched && errorMessage
  const value = ITEMS.find(({ id }) => id === (input.value ?? QuestionInvertTypeEnum.AUTO)) ?? null

  useEffect(() => {
    if (!isPreviewModalOpen || !htmlWithImagesForPreview) {
      setProcessedHtml('')
      return
    }

    setIsLoading(true)
    ;(async () => {
      const htmlUpd = await prepareHtmlWithImages(htmlWithImagesForPreview, input.value)
      setProcessedHtml(htmlUpd)
      setIsLoading(false)
    })()
  }, [isPreviewModalOpen, htmlWithImagesForPreview, input.value])

  const handlePreviewClick = (event: React.MouseEvent<HTMLDivElement>, isDarkMode: boolean) => {
    const target = event.target as HTMLElement
    const previewSpan = target.closest('span[data-preview-image]') as HTMLElement

    if (previewSpan) {
      const imageUrl = previewSpan.getAttribute('data-preview-image')
      const invertMode = previewSpan.getAttribute(DATA_ATTRIBUTE)

      if (imageUrl) {
        setImagePopover({
          anchorEl: previewSpan,
          imageUrl,
          invertMode: invertMode || QuestionInvertTypeEnum.NO,
          isDarkMode,
        })
      }
    }
  }

  const handleClosePopover = () => {
    setImagePopover(null)
  }

  return (
    <div css={classes.wrapper} className={className}>
      <span css={classes.title}>Invert {title} images for dark mode:</span>
      <Select
        {...input}
        placeholder="Auto/Yes/No"
        items={ITEMS}
        value={value}
        onChange={(value) => {
          form.change(name, value?.id ?? QuestionInvertTypeEnum.AUTO)
        }}
        error={Boolean(error)}
        formHelperText={error || undefined}
        css={classes.select}
      />
      {!!htmlWithImagesForPreview && (
        <>
          <IconButton size="small" onClick={() => setIsPreviewModalOpen(true)}>
            <PreviewIcon css={classes.icon} />
          </IconButton>
          <Modal open={isPreviewModalOpen} onClose={() => setIsPreviewModalOpen(false)}>
            <div css={classes.modal}>
              <div css={classes.modalTitle}>HTML preview for the light and dark mode:</div>
              {isLoading ? (
                <PageLoader />
              ) : (
                <div css={classes.content}>
                  <div css={classes.previewContainer}>
                    <div css={classes.previewLabel}>Light Mode</div>
                    <div
                      ref={lightModeRef}
                      css={[classes.htmlPreview, lightModePreviewStyles]}
                      dangerouslySetInnerHTML={{ __html: processedHtml }}
                      onClick={(e) => handlePreviewClick(e, false)}
                    />
                  </div>
                  <div css={classes.previewContainer}>
                    <div css={classes.previewLabel}>Dark Mode</div>
                    <div
                      ref={darkModeRef}
                      css={[classes.htmlPreview, darkModePreviewStyles]}
                      dangerouslySetInnerHTML={{ __html: processedHtml }}
                      onClick={(e) => handlePreviewClick(e, true)}
                    />
                  </div>
                </div>
              )}
              <Button fullWidth size="large" color="blue" onClick={() => setIsPreviewModalOpen(false)}>
                Okay
              </Button>
            </div>
          </Modal>
          <Popover
            open={Boolean(imagePopover)}
            anchorEl={imagePopover?.anchorEl}
            onClose={handleClosePopover}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <div css={classes.imagePopover(imagePopover?.isDarkMode, imagePopover?.invertMode)}>
              {imagePopover?.imageUrl && <img src={imagePopover.imageUrl} alt="Preview" />}
            </div>
          </Popover>
        </>
      )}
    </div>
  )
}
